/* eslint-disable comma-dangle */
import { createTheme, ThemeOptions } from '@mui/material/styles';

/**
 * PDF Maker 设计系统
 * 
 * 颜色理念：
 * - 主色：专业的蓝色系，象征信任和科技
 * - 辅助色：活力的紫色系，增添现代感
 * - 成功色：清新的绿色系
 * - 警告色：温暖的橙色系
 * - 错误色：柔和的红色系
 * - 中性色：优雅的灰色系
 */

// ==================== 颜色系统 ====================
const colors = {
  // 主色系 - 专业蓝
  primary: {
    50: '#E3F2FD',
    100: '#BBDEFB',
    200: '#90CAF9',
    300: '#64B5F6',
    400: '#42A5F5',
    500: '#2196F3',  // 主色
    600: '#1E88E5',
    700: '#1976D2',
    800: '#1565C0',
    900: '#0D47A1',
  },
  
  // 辅助色系 - 活力紫
  secondary: {
    50: '#F3E5F5',
    100: '#E1BEE7',
    200: '#CE93D8',
    300: '#BA68C8',
    400: '#AB47BC',
    500: '#9C27B0',  // 辅助色
    600: '#8E24AA',
    700: '#7B1FA2',
    800: '#6A1B9A',
    900: '#4A148C',
  },
  
  // 成功色系 - 清新绿
  success: {
    50: '#E8F5E9',
    100: '#C8E6C9',
    200: '#A5D6A7',
    300: '#81C784',
    400: '#66BB6A',
    500: '#4CAF50',  // 成功色
    600: '#43A047',
    700: '#388E3C',
    800: '#2E7D32',
    900: '#1B5E20',
  },
  
  // 警告色系 - 温暖橙
  warning: {
    50: '#FFF3E0',
    100: '#FFE0B2',
    200: '#FFCC80',
    300: '#FFB74D',
    400: '#FFA726',
    500: '#FF9800',  // 警告色
    600: '#FB8C00',
    700: '#F57C00',
    800: '#EF6C00',
    900: '#E65100',
  },
  
  // 错误色系 - 柔和红
  error: {
    50: '#FFEBEE',
    100: '#FFCDD2',
    200: '#EF9A9A',
    300: '#E57373',
    400: '#EF5350',
    500: '#F44336',  // 错误色
    600: '#E53935',
    700: '#D32F2F',
    800: '#C62828',
    900: '#B71C1C',
  },
  
  // 信息色系 - 天空蓝
  info: {
    50: '#E1F5FE',
    100: '#B3E5FC',
    200: '#81D4FA',
    300: '#4FC3F7',
    400: '#29B6F6',
    500: '#03A9F4',  // 信息色
    600: '#039BE5',
    700: '#0288D1',
    800: '#0277BD',
    900: '#01579B',
  },
  
  // 中性色系 - 优雅灰
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
  
  // 特殊色
  white: '#FFFFFF',
  black: '#000000',
};

// ==================== 圆角系统 ====================
const borderRadius = {
  none: 0,
  xs: 4,      // 超小圆角 - 用于小标签、徽章
  sm: 6,      // 小圆角 - 用于按钮、输入框
  md: 8,      // 中等圆角 - 用于卡片
  lg: 12,     // 大圆角 - 用于对话框、抽屉
  xl: 16,     // 超大圆角 - 用于大型容器
  '2xl': 20,  // 特大圆角
  '3xl': 24,  // 极大圆角
  full: 9999, // 完全圆形 - 用于头像、圆形按钮
};

// ==================== 阴影系统 ====================
const shadows = {
  xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
  none: 'none',
};

// ==================== 排版系统 ====================
const typography = {
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    '"Noto Sans"',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(','),
  
  // 字体大小
  fontSize: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem',// 30px
    '4xl': '2.25rem', // 36px
    '5xl': '3rem',    // 48px
  },
  
  // 字重
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  
  // 行高
  lineHeight: {
    none: 1,
    tight: 1.25,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
  },
};

// ==================== 主题配置 ====================
const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: colors.primary[600],
      light: colors.primary[400],
      dark: colors.primary[800],
      contrastText: colors.white,
    },
    secondary: {
      main: colors.secondary[500],
      light: colors.secondary[300],
      dark: colors.secondary[700],
      contrastText: colors.white,
    },
    success: {
      main: colors.success[500],
      light: colors.success[300],
      dark: colors.success[700],
      contrastText: colors.white,
    },
    warning: {
      main: colors.warning[500],
      light: colors.warning[300],
      dark: colors.warning[700],
      contrastText: colors.white,
    },
    error: {
      main: colors.error[500],
      light: colors.error[300],
      dark: colors.error[700],
      contrastText: colors.white,
    },
    info: {
      main: colors.info[500],
      light: colors.info[300],
      dark: colors.info[700],
      contrastText: colors.white,
    },
    background: {
      default: colors.grey[50],
      paper: colors.white,
    },
    text: {
      primary: colors.grey[900],
      secondary: colors.grey[700],
      disabled: colors.grey[400],
    },
    divider: colors.grey[200],
  },
  
  typography: {
    fontFamily: typography.fontFamily,
    fontSize: 14,
    h1: {
      fontSize: typography.fontSize['4xl'],
      fontWeight: typography.fontWeight.bold,
      lineHeight: typography.lineHeight.tight,
    },
    h2: {
      fontSize: typography.fontSize['3xl'],
      fontWeight: typography.fontWeight.bold,
      lineHeight: typography.lineHeight.tight,
    },
    h3: {
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.snug,
    },
    h4: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.snug,
    },
    h5: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.medium,
      lineHeight: typography.lineHeight.normal,
    },
    h6: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.medium,
      lineHeight: typography.lineHeight.normal,
    },
    body1: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.regular,
      lineHeight: typography.lineHeight.normal,
    },
    body2: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.regular,
      lineHeight: typography.lineHeight.normal,
    },
    button: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      textTransform: 'none', // 按钮文字不自动大写
    },
    caption: {
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.regular,
      lineHeight: typography.lineHeight.normal,
    },
  },
  
  shape: {
    borderRadius: borderRadius.md, // 默认圆角
  },
  
  spacing: 8, // 基础间距单位
  
  shadows: [
    'none',
    shadows.xs,
    shadows.sm,
    shadows.sm,
    shadows.md,
    shadows.md,
    shadows.md,
    shadows.lg,
    shadows.lg,
    shadows.lg,
    shadows.xl,
    shadows.xl,
    shadows.xl,
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
  ],
  
  components: {
    // ==================== Button 按钮 ====================
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.sm,
          padding: '8px 16px',
          fontSize: typography.fontSize.sm,
          fontWeight: typography.fontWeight.medium,
          textTransform: 'none',
          boxShadow: 'none',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: shadows.sm,
            transform: 'translateY(-1px)',
          },
        },
        sizeLarge: {
          padding: '12px 24px',
          fontSize: typography.fontSize.base,
          borderRadius: borderRadius.md,
        },
        sizeSmall: {
          padding: '6px 12px',
          fontSize: typography.fontSize.xs,
          borderRadius: borderRadius.xs,
        },
      },
    },
    
    // ==================== Card 卡片 ====================
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.lg,
          boxShadow: shadows.sm,
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow: shadows.md,
          },
        },
      },
    },
    
    // ==================== Paper 纸张 ====================
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.md,
        },
        elevation1: {
          boxShadow: shadows.sm,
        },
        elevation2: {
          boxShadow: shadows.md,
        },
        elevation3: {
          boxShadow: shadows.lg,
        },
      },
    },
    
    // ==================== TextField 文本输入框 ====================
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: borderRadius.sm,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: shadows.xs,
            },
            '&.Mui-focused': {
              boxShadow: shadows.sm,
            },
          },
        },
      },
    },
    
    // ==================== Dialog 对话框 ====================
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: borderRadius.xl,
          boxShadow: shadows['2xl'],
        },
      },
    },
    
    // ==================== Drawer 抽屉 ====================
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0, // 侧边栏不需要圆角
          boxShadow: shadows.lg,
        },
      },
    },
    
    // ==================== Chip 标签 ====================
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.full,
          fontWeight: typography.fontWeight.medium,
        },
        sizeSmall: {
          fontSize: typography.fontSize.xs,
        },
      },
    },
    
    // ==================== AppBar 应用栏 ====================
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: shadows.sm,
        },
      },
    },
    
    // ==================== List 列表 ====================
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.sm,
          margin: '4px 8px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: colors.primary[50],
          },
          '&.Mui-selected': {
            backgroundColor: colors.primary[100],
            '&:hover': {
              backgroundColor: colors.primary[200],
            },
          },
        },
      },
    },
    
    // ==================== Table 表格 ====================
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: colors.grey[200],
        },
        head: {
          fontWeight: typography.fontWeight.semibold,
          backgroundColor: colors.grey[50],
        },
      },
    },
    
    // ==================== Alert 警告框 ====================
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.md,
          boxShadow: shadows.sm,
        },
      },
    },
    
    // ==================== Switch 开关 ====================
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: 8,
        },
        thumb: {
          boxShadow: shadows.sm,
        },
      },
    },
  },
};

// 创建主题
const theme = createTheme(themeOptions);

// 导出主题和设计系统常量
export default theme;

export {
  colors,
  borderRadius,
  shadows,
  typography,
  themeOptions,
};
